import axios from 'axios'
import { ExternalLink, FileText } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Banner } from '@/components/ui/banner'
import { Button } from '@/components/ui/button'
import { SkeletonRows } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { documentContentUrl, type PreviewDocument } from './previewDocument'

interface Props {
  doc: PreviewDocument
  /** One of several in a scrolling column, rather than the only document in the pane. */
  stacked: boolean
}

/**
 * One document rendered read-only, with its own name and page controls.
 *
 * Fetched with axios rather than pointed at directly; the resulting blob URL
 * is same-origin, so the iframe renders it natively.
 */
export function DocumentPreview({ doc, stacked }: Props) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const contentUrl = documentContentUrl(doc)

  useEffect(() => {
    let url: string | null = null
    let cancelled = false
    setObjectUrl(null)
    setFailed(false)

    axios
      .get(contentUrl, { responseType: 'blob' })
      .then((response) => {
        if (cancelled) return
        url = URL.createObjectURL(response.data as Blob)
        setObjectUrl(url)
      })
      .catch(() => !cancelled && setFailed(true))

    return () => {
      cancelled = true
      if (url) URL.revokeObjectURL(url)
    }
  }, [contentUrl])

  return (
    <div className={cn('flex min-h-0 flex-col gap-2', !stacked && 'flex-1')}>
      {/* Sticky when stacked: the viewer swallows the wheel, so this strip is how you scroll past it. */}
      <div
        className={cn(
          'flex items-center gap-2',
          stacked && 'bg-muted sticky top-0 z-10 -mx-1 rounded-sm px-1 py-1'
        )}
      >
        <FileText className="text-muted-foreground size-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{doc.fileName || 'Document'}</span>
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={!objectUrl}
          onClick={() => objectUrl && window.open(objectUrl, '_blank', 'noopener')}
          aria-label={`Open ${doc.fileName || 'document'} in a new tab`}
          title="Open in new tab"
        >
          <ExternalLink className="size-4" />
        </Button>
      </div>

      <div className={cn('min-h-0', stacked ? 'h-[78vh] shrink-0' : 'flex-1')}>
        {failed ? (
          <Banner variant="error" small>
            This document couldn’t be loaded.
          </Banner>
        ) : objectUrl ? (
          <iframe
            key={objectUrl}
            src={`${objectUrl}#view=FitH&navpanes=0&pagemode=none`}
            title={doc.fileName || 'Document preview'}
            className="bg-background h-full w-full rounded-sm border"
          />
        ) : (
          <SkeletonRows rows={6} rowClassName="h-16" />
        )}
      </div>
    </div>
  )
}
